/**
 * App-wide preferences, kept in `localStorage`.
 *
 * Synchronous on purpose: the theme and the panel layout have to be known
 * before the first paint, and an async read there would cause a visible flash.
 * The payload is a few hundred bytes, so the main-thread cost is negligible.
 *
 * Reads are total: any missing, mistyped, out-of-range or unknown field is
 * replaced by its default rather than rejected, because a preferences file the
 * app refuses to load is indistinguishable from a broken app.
 */
import { storageKey } from '@/config/branding';
import type { FlowDirection } from '@/graph/model/types';

export type ThemePreference = 'system' | 'light' | 'dark';
export type LocalePreference = 'system' | 'en' | 'pt';
export type PanelLayout = 'split' | 'code' | 'diagram';
export type FunctionDisplay = 'expanded' | 'collapsed';

export interface AppPreferences {
  readonly theme: ThemePreference;
  readonly locale: LocalePreference;
  readonly panelLayout: PanelLayout;
  /** Puts the diagram on the left and the editor on the right. */
  readonly swapPanels: boolean;
  /** Fraction of the width given to the first panel. */
  readonly splitRatio: number;
  readonly editorFontSize: number;
  readonly editorFontFamily: string;
  /** When false the diagram only refreshes on an explicit run. */
  readonly autoUpdate: boolean;
  /** Idle delay before recompiling after a keystroke. */
  readonly debounceMs: number;
  readonly direction: FlowDirection;
  readonly showGrid: boolean;
  readonly showMinimap: boolean;
  readonly reduceMotion: boolean;
  readonly functionDisplay: FunctionDisplay;
  readonly yesLabel: string;
  readonly noLabel: string;
  /** Gap between nodes within a rank, in px. */
  readonly nodeSpacing: number;
  /** Gap between ranks, in px. */
  readonly rankSpacing: number;
  /** Reopened on the next visit; `null` starts on the welcome screen. */
  readonly lastProjectId: string | null;
  /** Node id to CSS colour, from the inspector's colour picker. */
  readonly nodeColorOverrides: Readonly<Record<string, string>>;
}

const PREFERENCES_KEY = storageKey('preferences');

export const DEFAULT_PREFERENCES: AppPreferences = Object.freeze({
  theme: 'system' as ThemePreference,
  locale: 'system' as LocalePreference,
  panelLayout: 'split' as PanelLayout,
  swapPanels: false,
  splitRatio: 0.5,
  editorFontSize: 14,
  editorFontFamily:
    "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
  autoUpdate: true,
  debounceMs: 250,
  direction: 'top-to-bottom' as FlowDirection,
  showGrid: false,
  showMinimap: true,
  reduceMotion: false,
  // Expanded by default: a `call` should show what it calls, which is what
  // makes the diagram read as one flow rather than a set of fragments.
  functionDisplay: 'expanded' as FunctionDisplay,
  yesLabel: 'Yes',
  noLabel: 'No',
  nodeSpacing: 48,
  rankSpacing: 64,
  lastProjectId: null,
  nodeColorOverrides: Object.freeze({}) as Readonly<Record<string, string>>,
});

/** Bounds the UI also enforces; kept here so a hand-edited value cannot escape them. */
export const PREFERENCE_LIMITS = Object.freeze({
  splitRatio: { min: 0.2, max: 0.8 },
  editorFontSize: { min: 11, max: 22 },
  debounceMs: { min: 0, max: 2000 },
  nodeSpacing: { min: 16, max: 200 },
  rankSpacing: { min: 16, max: 320 },
});

const MAX_LABEL_LENGTH = 32;
const MAX_FONT_FAMILY_LENGTH = 200;
const MAX_COLOR_OVERRIDES = 500;
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/* ------------------------------------------------------------------ *
 * Coercion helpers
 * ------------------------------------------------------------------ */

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function pickOption<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function pickNumber(
  value: unknown,
  { min, max }: { min: number; max: number },
  fallback: number,
  round = false,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const clamped = Math.min(max, Math.max(min, value));
  return round ? Math.round(clamped) : clamped;
}

function pickString(value: unknown, fallback: string, maxLength: number): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (trimmed.length === 0) return fallback;
  return trimmed.slice(0, maxLength);
}

function pickColorOverrides(value: unknown): Readonly<Record<string, string>> {
  if (!isRecordObject(value)) return DEFAULT_PREFERENCES.nodeColorOverrides;
  const result: Record<string, string> = {};
  let kept = 0;
  for (const [key, color] of Object.entries(value)) {
    if (kept >= MAX_COLOR_OVERRIDES) break;
    if (key.length === 0 || typeof color !== 'string' || !HEX_COLOR.test(color)) continue;
    result[key] = color;
    kept += 1;
  }
  return result;
}

const THEMES: readonly ThemePreference[] = ['system', 'light', 'dark'];
const LOCALES: readonly LocalePreference[] = ['system', 'en', 'pt'];
const LAYOUTS: readonly PanelLayout[] = ['split', 'code', 'diagram'];
const DIRECTIONS: readonly FlowDirection[] = ['top-to-bottom', 'left-to-right'];
const FUNCTION_DISPLAYS: readonly FunctionDisplay[] = ['expanded', 'collapsed'];

/**
 * Builds a valid `AppPreferences` from arbitrary input. Only known keys are
 * read, so anything a previous version wrote and this one dropped disappears on
 * the next save.
 */
export function normalizePreferences(input: unknown): AppPreferences {
  const raw: Record<string, unknown> = isRecordObject(input) ? input : {};
  const d = DEFAULT_PREFERENCES;
  const limits = PREFERENCE_LIMITS;

  return {
    theme: pickOption(raw.theme, THEMES, d.theme),
    locale: pickOption(raw.locale, LOCALES, d.locale),
    panelLayout: pickOption(raw.panelLayout, LAYOUTS, d.panelLayout),
    swapPanels: pickBoolean(raw.swapPanels, d.swapPanels),
    splitRatio: pickNumber(raw.splitRatio, limits.splitRatio, d.splitRatio),
    editorFontSize: pickNumber(raw.editorFontSize, limits.editorFontSize, d.editorFontSize, true),
    editorFontFamily: pickString(raw.editorFontFamily, d.editorFontFamily, MAX_FONT_FAMILY_LENGTH),
    autoUpdate: pickBoolean(raw.autoUpdate, d.autoUpdate),
    debounceMs: pickNumber(raw.debounceMs, limits.debounceMs, d.debounceMs, true),
    direction: pickOption(raw.direction, DIRECTIONS, d.direction),
    showGrid: pickBoolean(raw.showGrid, d.showGrid),
    showMinimap: pickBoolean(raw.showMinimap, d.showMinimap),
    reduceMotion: pickBoolean(raw.reduceMotion, d.reduceMotion),
    functionDisplay: pickOption(raw.functionDisplay, FUNCTION_DISPLAYS, d.functionDisplay),
    yesLabel: pickString(raw.yesLabel, d.yesLabel, MAX_LABEL_LENGTH),
    noLabel: pickString(raw.noLabel, d.noLabel, MAX_LABEL_LENGTH),
    nodeSpacing: pickNumber(raw.nodeSpacing, limits.nodeSpacing, d.nodeSpacing, true),
    rankSpacing: pickNumber(raw.rankSpacing, limits.rankSpacing, d.rankSpacing, true),
    lastProjectId: typeof raw.lastProjectId === 'string' ? raw.lastProjectId : null,
    nodeColorOverrides: pickColorOverrides(raw.nodeColorOverrides),
  };
}

/* ------------------------------------------------------------------ *
 * Storage
 * ------------------------------------------------------------------ */

/** Resolved per call: tests swap the global, and browsers can revoke access. */
function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Access itself throws when the origin is sandboxed without allow-same-origin.
    return null;
  }
}

/** Never throws. Falls back to `DEFAULT_PREFERENCES` for missing or corrupt data. */
export function loadPreferences(): AppPreferences {
  const store = storage();
  if (!store) return DEFAULT_PREFERENCES;

  let raw: string | null;
  try {
    raw = store.getItem(PREFERENCES_KEY);
  } catch {
    return DEFAULT_PREFERENCES;
  }
  if (raw === null) return DEFAULT_PREFERENCES;

  try {
    return normalizePreferences(JSON.parse(raw));
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Never throws. Preferences are a convenience, not data the user created, so a
 * full disk or a blocked storage API is not worth interrupting them over — the
 * session simply keeps its in-memory values.
 */
export function savePreferences(prefs: AppPreferences): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PREFERENCES_KEY, JSON.stringify(normalizePreferences(prefs)));
  } catch {
    // Quota exceeded, or storage denied. Nothing actionable.
  }
}

/** Removes the stored preferences; the next load returns the defaults. */
export function clearPreferences(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(PREFERENCES_KEY);
  } catch {
    // Same rationale as `savePreferences`.
  }
}
